import { useRef, useState } from 'react';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode, Ref, SVGAttributes } from 'react';
import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
  type Placement,
} from '@floating-ui/react';
import { ICON_VIEWBOX, ICONS, type IconName } from './icons';

/**
 * The primitives slice 4 leans on.
 *
 * These belong to slice 2 and are here only as far as the games list and create-game need them
 * — a list has to sit in a panel, an empty list has to say what to do next, and a create form
 * needs a field, an input and a select. They are faithful ports of the handoff components of
 * the same names, so slice 2 should finish them (variants, icons, the rest of the props)
 * rather than start again.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'soft' | 'ghost' | 'brand' | 'success' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  icon?: IconName;
  iconAfter?: IconName;
  /** A plain prop in React 19 — dialogs need it to park initial focus on Cancel. */
  ref?: Ref<HTMLButtonElement>;
  children?: ReactNode;
}

const ICON_FOR_SIZE: Record<ButtonSize, number> = { sm: 18, md: 20, lg: 24 };

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  iconAfter,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  const classes = [`bf-btn bf-btn-${variant} bf-btn-${size}`, fullWidth ? 'bf-btn-full' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <button type={type} className={classes} {...rest}>
      {icon ? <Icon name={icon} size={ICON_FOR_SIZE[size]} /> : null}
      {children}
      {iconAfter ? <Icon name={iconAfter} size={ICON_FOR_SIZE[size]} /> : null}
    </button>
  );
}

export type IconButtonVariant = 'solid' | 'soft' | 'cream' | 'ghost' | 'muted';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconName;
  variant?: IconButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  /** Required in practice: an icon-only control with no label is unusable without sight. */
  label: string;
}

const ICON_BUTTON_SIZE = { sm: 36, md: 48, lg: 64 } as const;

export function IconButton({
  icon,
  variant = 'soft',
  size = 'md',
  label,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const d = ICON_BUTTON_SIZE[size];
  const classes = [`bf-iconbtn bf-iconbtn-${variant} bf-iconbtn-${size}`, className ?? ''].filter(Boolean).join(' ');
  return (
    <button type={type} className={classes} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size={Math.round(d * 0.5)} />
    </button>
  );
}

export interface IconProps extends SVGAttributes<SVGSVGElement> {
  /** Material Symbols Rounded glyph name, e.g. "expand_more". */
  name: IconName;
  /** Pixel size. Web UI uses 20–24. */
  size?: number;
}

/**
 * Material Symbols Rounded, filled, drawn from `icons.ts`.
 *
 * `aria-hidden` by design: an icon is decoration and the label belongs on the control that
 * holds it, so an icon-only control carries its own `aria-label`. Fills with `currentColor`,
 * so it takes the colour of whatever it sits in exactly as the icon font did.
 */
export function Icon({ name, size = 24, className, ...rest }: IconProps) {
  const path = ICONS[name];
  if (!path) return null;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={ICON_VIEWBOX}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flex: '0 0 auto' }}
      {...rest}
    >
      <path d={path} />
    </svg>
  );
}

/**
 * BoardFlight's mark at rest — four tiles, the top-right one purple — in the 288-unit coordinates
 * of the drawable it was first drawn as (`brand_mark.xml` in the retired Board client, whose
 * source is in git history), cropped to the tiles. The three ink tiles fill
 * with `currentColor`, so the mark takes the wordmark's colour in either theme; the purple tile is
 * the brand's on every surface.
 *
 * Sized by its CSS (`.bf-brandmark`, `1em`), so it scales with whichever wordmark it leads.
 * Decorative: the word beside it is the link's name.
 */
export function BrandMark({ className }: { className?: string }) {
  return (
    <svg
      className={['bf-brandmark', className ?? ''].filter(Boolean).join(' ')}
      viewBox="70 70 148 148"
      aria-hidden="true"
      focusable="false"
    >
      <path fill="currentColor" d="M92,77 H122 A15,15 0 0 1 137,92 V122 A15,15 0 0 1 122,137 H92 A15,15 0 0 1 77,122 V92 A15,15 0 0 1 92,77 Z" />
      <path fill="currentColor" d="M92,151 H122 A15,15 0 0 1 137,166 V196 A15,15 0 0 1 122,211 H92 A15,15 0 0 1 77,196 V166 A15,15 0 0 1 92,151 Z" />
      <path fill="currentColor" d="M166,151 H196 A15,15 0 0 1 211,166 V196 A15,15 0 0 1 196,211 H166 A15,15 0 0 1 151,196 V166 A15,15 0 0 1 166,151 Z" />
      <path fill="var(--purple)" d="M166,77 H196 A15,15 0 0 1 211,92 V122 A15,15 0 0 1 196,137 H166 A15,15 0 0 1 151,122 V92 A15,15 0 0 1 166,77 Z" />
    </svg>
  );
}

/* `title` is omitted before being redeclared: the DOM one is the tooltip string, and a panel
   heading is a node. Same for EmptyState below. */
export interface GroupedPanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  note?: ReactNode;
  action?: ReactNode;
  footer?: ReactNode;
  /** The body runs edge to edge — data lists own their row padding. */
  flush?: boolean;
  /**
   * The *why* behind the panel, on an (i) beside the title (`Explainer`).
   *
   * `note` is the one visible sentence — what the panel is or what to do in it. Everything a
   * reader might want to know *before trusting it* — how it works underneath, the edge cases, the
   * reason it is shaped this way — goes here, closed until asked for. A panel whose only
   * explanation is this one draws the icon and no paragraph at all.
   */
  info?: ReactNode;
  children?: ReactNode;
}

/** The only container on the web app. Forms, data lists, reveals and facts all sit in one. */
export function GroupedPanel({
  title,
  note,
  info,
  action,
  footer,
  flush = false,
  className,
  children,
  ...rest
}: GroupedPanelProps) {
  return (
    <section className={className ? `bf-panel ${className}` : 'bf-panel'} {...rest}>
      {title || note || action ? (
        <header className={flush ? 'bf-panel-head is-flush' : 'bf-panel-head'}>
          <div className="bf-panel-headtext">
            {title ? (
              <div className="bf-panel-titlerow">
                <h2 className="bf-panel-title">{title}</h2>
                {info ? (
                  <Explainer label={typeof title === 'string' ? `About ${title}` : 'About this section'}>
                    {info}
                  </Explainer>
                ) : null}
              </div>
            ) : null}
            {note ? <p className="bf-panel-note">{note}</p> : null}
          </div>
          {action}
        </header>
      ) : null}
      <div className={flush ? 'bf-panel-body is-flush' : 'bf-panel-body'}>{children}</div>
      {footer ? <footer className="bf-panel-foot">{footer}</footer> : null}
    </section>
  );
}

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  /** Drop the surface when the empty state is already inside a panel. */
  bare?: boolean;
  /** The longer answer to "why is it empty?", on an (i) at the end of the body sentence. */
  info?: ReactNode;
}

/** Never a bare "No items" — a sentence saying why it is empty and what to do next. */
export function EmptyState({ title, body, action, bare = false, info, className, ...rest }: EmptyStateProps) {
  const classes = ['bf-empty', bare ? 'is-bare' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <div className={classes} {...rest}>
      <h2 className="bf-empty-title">{title}</h2>
      {body || info ? (
        <p className="bf-empty-body">
          {body}
          {info ? (
            <>
              {' '}
              <Explainer label="More about this">{info}</Explainer>
            </>
          ) : null}
        </p>
      ) : null}
      {action}
    </div>
  );
}

export interface DisclosureProps {
  /** The row's text, closed: "What this records", "How priority is used". */
  label: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * A block that is too much for an `Explainer`'s popover — a document rather than an answer:
 * the privacy disclosures under the analytics switches, the rules of model priority — closed
 * by default and opened in place.
 *
 * Native `<details>`: it works before React runs, every word is in the DOM for find-in-page,
 * and the keyboard and the screen reader already know what it is.
 */
export function Disclosure({ label, defaultOpen = false, className, children }: DisclosureProps) {
  return (
    <details className={className ? `bf-disclosure ${className}` : 'bf-disclosure'} open={defaultOpen || undefined}>
      <summary className="bf-disclosure-summary">
        <span>{label}</span>
        <Icon name="expand_more" size={18} />
      </summary>
      <div className="bf-disclosure-body">{children}</div>
    </details>
  );
}

/**
 * A small state pill — "default", "revoked", "Passed", "processing".
 *
 * Not StatusChip: that one is the seven install states a tester's Board reports, and its
 * labels come from a fixed map. This is the general marker the lists use for a row's own
 * state, and the tone carries a dot only when it means good or bad.
 */
export function Marker({ tone = 'neutral', children }: { tone?: 'neutral' | 'good' | 'bad'; children: ReactNode }) {
  return (
    <span className={`bf-marker is-${tone}`}>
      {tone !== 'neutral' ? <span className="bf-marker-dot" /> : null}
      {children}
    </span>
  );
}

export interface GameIconProps extends HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  name?: string;
  size?: number;
  radius?: number;
}

/** A list of package ids all looks the same; a list of icons does not. */
export function GameIcon({ src, name = '', size = 48, radius, className, style, ...rest }: GameIconProps) {
  const letter = (name.trim()[0] || '?').toUpperCase();
  return (
    <div
      className={className ? `bf-gameicon ${className}` : 'bf-gameicon'}
      // Proportions of the box, not scale steps: the corner and the letter are a fixed
      // fraction of whatever size the caller asked for, so one icon at 44px and another at
      // 96px are the same shape rather than the same numbers.
      style={{ width: size, height: size, borderRadius: radius ?? Math.round(size * 0.26), ...style }}
      {...rest}
    >
      {src ? (
        <img src={src} alt="" />
      ) : (
        <span className="bf-gameicon-letter" style={{ fontSize: Math.round(size * 0.46) }}>
          {letter}
        </span>
      )}
    </div>
  );
}

export interface SwitchRowProps {
  /** The setting's name. */
  label: string;
  /** One fixed line under the name: what the setting does. Never the state — that is `state`. */
  description?: ReactNode;
  /** The state as a word in a marker beside the name: "Reporting", "Off", "Needs analytics". */
  state?: { label: string; tone?: 'neutral' | 'good' | 'bad' };
  /** The longer answer, on an (i) beside the name. */
  info?: ReactNode;
  /** A sub-setting, drawn under the one it depends on. */
  indent?: boolean;
  /** The switch itself, or whatever control sits at the end of the row. */
  children?: ReactNode;
}

/**
 * A setting and its switch, on one row.
 *
 * The description is fixed and the state is a marker, kept apart on purpose: a line that rewrote
 * itself when the switch moved ("Not reporting" → "Builds published here are patched…") was two
 * things in one place, and neither could be scanned down a list of rows.
 */
export function SwitchRow({ label, description, state, info, indent = false, children }: SwitchRowProps) {
  return (
    <div className={indent ? 'bf-switch-row is-indented' : 'bf-switch-row'}>
      <span className="bf-switch-text">
        <span className="bf-cell-text bf-switch-label">
          {label}
          {state ? <Marker tone={state.tone ?? 'neutral'}>{state.label}</Marker> : null}
          {info ? <Explainer label={`About ${label}`}>{info}</Explainer> : null}
        </span>
        {description ? <span className="bf-switch-desc">{description}</span> : null}
      </span>
      {children}
    </div>
  );
}

export interface ExplainerProps {
  /** The explanation: a sentence or two, a short list, at most two paragraphs. Longer is a `Disclosure`. */
  children: ReactNode;
  /** What the icon is about, as its accessible name: "About the package id". */
  label: string;
  /** Where it prefers to open. It flips and slides from here as the viewport requires. */
  placement?: Placement;
}

/** Long enough not to fire while a pointer crosses a row of them; short enough to feel like an answer. */
const EXPLAINER_OPEN_DELAY = 250;
const EXPLAINER_CLOSE_DELAY = 120;

/**
 * The (i) that holds everything a screen does not say out loud.
 *
 * The dashboard's rule (`docs/dashboard-ux-plan.md` §0) is one visible sentence per panel,
 * field or empty state, and the rest one click away. This is the one click. Hover or focus
 * opens it as a tooltip would; a press keeps it open, so a two-sentence answer can be read, and
 * its text selected, without holding a pointer still. Escape, a press outside, or a second
 * press closes it.
 *
 * It replaces `InfoHint`, which was a browser `title`: a second to appear, unstyled, gone on
 * touch, and silent to a screen reader. This one is positioned by the same engine as the
 * playground's `Tooltip`, drawn with the kit's tokens, and described to assistive tech through
 * `aria-describedby` while it is open.
 */
export function Explainer({ children, label, placement = 'bottom-start' }: ExplainerProps) {
  const [open, setOpen] = useState(false);
  // A press pins it: hover stops closing it, and only a dismissal or another press does.
  const [pinned, setPinned] = useState(false);
  const pointer = useRef<SVGSVGElement | null>(null);

  const floating = useFloating({
    open,
    onOpenChange: (next) => {
      setOpen(next);
      if (!next) setPinned(false);
    },
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 }), arrow({ element: pointer })],
  });

  const interactions = useInteractions([
    useHover(floating.context, {
      enabled: !pinned,
      delay: { open: EXPLAINER_OPEN_DELAY, close: EXPLAINER_CLOSE_DELAY },
      // The pointer may cross from the icon into the text to select it.
      handleClose: safePolygon(),
      mouseOnly: true,
    }),
    useFocus(floating.context, { enabled: !pinned, visibleOnly: true }),
    useDismiss(floating.context),
    useRole(floating.context, { role: 'tooltip' }),
  ]);

  const motion = useTransitionStyles(floating.context, {
    duration: { open: 120, close: 80 },
    initial: { opacity: 0, transform: 'translateY(-2px)' },
  });

  return (
    <>
      <button
        ref={floating.refs.setReference}
        type="button"
        className={pinned ? 'bf-explainer is-pinned' : 'bf-explainer'}
        aria-label={label}
        aria-expanded={open}
        {...interactions.getReferenceProps({
          onClick: (event) => {
            event.preventDefault();
            if (pinned) {
              setPinned(false);
              setOpen(false);
            } else {
              setPinned(true);
              setOpen(true);
            }
          },
        })}
      >
        <Icon name="info" size={16} />
      </button>

      {motion.isMounted ? (
        <FloatingPortal>
          {/* Two elements for the same reason `Tooltip` has two: position on the outer box,
              motion on the inner one, so the two transforms never fight. */}
          <div
            ref={floating.refs.setFloating}
            className="bf-tip-layer"
            style={floating.floatingStyles}
            {...interactions.getFloatingProps()}
          >
            <div className="bf-explain" style={motion.styles}>
              <div className="bf-explain-text">{children}</div>
              <FloatingArrow
                ref={pointer}
                context={floating.context}
                className="bf-explain-arrow"
                width={12}
                height={6}
              />
            </div>
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
}

export interface FieldProps extends HTMLAttributes<HTMLDivElement> {
  label?: ReactNode;
  /** The one visible sentence under the control: the format, the default, what to type. */
  hint?: ReactNode;
  /**
   * The reason behind the field, on an (i) beside the label (`Explainer`) — for the questions a
   * hint cannot answer in a line because they are about the field itself: why it is read-only,
   * what changing it costs, what the rule behind the format is.
   */
  info?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor?: string;
  children?: ReactNode;
}

/** Label, control, hint. The hint is often a full sentence and carries real information. */
export function Field({
  label,
  hint,
  info,
  error,
  required = false,
  htmlFor,
  className,
  children,
  ...rest
}: FieldProps) {
  return (
    <div className={className ? `bf-field ${className}` : 'bf-field'} {...rest}>
      {label ? (
        /* The icon sits beside the `<label>`, never inside it: a button within a label is
           activated a second time by the label itself, and the click would land on the
           control the label points at. */
        <div className="bf-field-labelrow">
          <label className="bf-field-label" htmlFor={htmlFor}>
            {label}
            {required ? <span className="bf-field-required"> *</span> : null}
          </label>
          {info ? (
            <Explainer label={typeof label === 'string' ? `About ${label}` : 'About this field'}>
              {info}
            </Explainer>
          ) : null}
        </div>
      ) : null}
      {children}
      {error ? (
        <p className="bf-field-error">{error}</p>
      ) : hint ? (
        <p className="bf-field-hint">{hint}</p>
      ) : null}
    </div>
  );
}

export interface TextInputProps {
  id?: string;
  value?: string;
  placeholder?: string;
  mono?: boolean;
  multiline?: boolean;
  rows?: number;
  invalid?: boolean;
  disabled?: boolean;
  /**
   * Shown but not editable — and deliberately not `disabled`: a read-only value is usually
   * one you came to copy, so it stays selectable and in the tab order. `disabled` would take
   * both away and say "not now" where this means "not ever".
   */
  readOnly?: boolean;
  required?: boolean;
  autoComplete?: string;
  /** Only for the first field of a modal, which has nothing behind it to steal focus from. */
  autoFocus?: boolean;
  /**
   * `password` masks the value and stops it being read off a shoulder or a screen share. Only
   * the two the kit has a use for: a `type` this component forwards blindly would quietly turn
   * into `type="number"` somewhere, whose arrows and locale-dependent parsing are their own
   * problem. Ignored when `multiline` — a textarea has no type.
   */
  type?: 'text' | 'password';
  /**
   * Which keyboard a phone offers — and nothing else. Unlike `type`, it changes no validation and
   * no parsing, so the value is still a string checked by whoever reads it.
   */
  inputMode?: 'text' | 'email' | 'numeric';
  /** The name, for a field with no `<label>` beside it — a cell in an editable table. */
  'aria-label'?: string;
  onChange?: (value: string) => void;
}

export function TextInput({
  mono = false,
  multiline = false,
  rows = 5,
  invalid = false,
  type = 'text',
  onChange,
  ...rest
}: TextInputProps) {
  const classes = ['bf-input', mono ? 'is-mono' : '', invalid ? 'is-invalid' : '', rest.readOnly ? 'is-readonly' : '']
    .filter(Boolean)
    .join(' ');
  const shared = {
    className: classes,
    'aria-invalid': invalid || undefined,
    onChange: (event: { target: { value: string } }) => onChange?.(event.target.value),
    ...rest,
  };
  return multiline ? <textarea rows={rows} {...shared} /> : <input type={type} {...shared} />;
}

export interface ToggleProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  /** Labels the switch for anyone who cannot see which side the knob is on. */
  label: string;
}

/** A switch, at web size — the handoff's defaults (96×52) are the console's. */
export function Toggle({ checked = false, onChange, disabled = false, label }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={checked ? 'bf-toggle is-on' : 'bf-toggle'}
      onClick={() => onChange?.(!checked)}
    >
      <span className="bf-toggle-knob" />
    </button>
  );
}

export type SelectOption = string | { value: string; label: string; disabled?: boolean };

export interface SelectProps {
  id?: string;
  value?: string;
  options?: SelectOption[];
  disabled?: boolean;
  /** Drawn as `TextInput`'s is: a red edge, and `aria-invalid` for a screen reader. */
  invalid?: boolean;
  onChange?: (value: string) => void;
  /** The name, for a select that sits in a toolbar with no `<label>` beside it. */
  'aria-label'?: string;
}

/** Native, which is right for a closed set — the platform's own list beats a rebuilt one. */
export function Select({ value, options = [], disabled = false, invalid = false, onChange, ...rest }: SelectProps) {
  return (
    <div className="bf-select-wrap">
      <select
        className={invalid ? 'bf-select is-invalid' : 'bf-select'}
        aria-invalid={invalid || undefined}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.value)}
        {...rest}
      >
        {options.map((option) => {
          const item = typeof option === 'string' ? { value: option, label: option, disabled: false } : option;
          return (
            <option key={item.value} value={item.value} disabled={item.disabled}>
              {item.label}
            </option>
          );
        })}
      </select>
      <span className="bf-select-chevron">
        <Icon name="expand_more" size={22} />
      </span>
    </div>
  );
}
